import type { IRiskTreatmentPlanActionDisplayState, IRiskTreatmentPlanStatus } from '@/lib/constants/riskTreatmentPlans'

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

export const RISK_TREATMENT_PLANS = {
  SEARCH: 'Search plans, risks or owners',
  ALL: 'All',
  NEW_PLAN: 'New treatment plan',
  STATUS: {
    onTrack: 'On track',
    atRisk: 'At risk',
    overdue: 'Overdue',
    done: 'Completed',
  } satisfies Record<IRiskTreatmentPlanStatus, string>,
  ACTION_STATE: {
    open: 'Not started',
    inProgress: 'In progress',
    done: 'Done',
    overdue: 'Overdue',
  } satisfies Record<IRiskTreatmentPlanActionDisplayState, string>,
  NO_PLANS: 'No plans match these filters',
  RISK_COUNT: (count: number) => plural(count, 'risk', 'risks'),
  ACTIONS_PROGRESS: (done: number, total: number) => `${done}/${total} actions`,
  UNCOVERED: {
    TITLE: (count: number) => `${plural(count, 'risk', 'risks')} without a plan`,
    DESCRIPTION: 'Open risks to mitigate that no plan covers yet',
  },
  DETAIL: {
    LINK_RISKS: 'Link risks',
    OWNER: 'Plan owner',
    TARGET_DATE: 'Target date',
    NEXT_FOLLOW_UP: 'Next follow-up',
    PROGRESS: 'Progress',
    RISKS: (count: number) => `Risks covered by this plan (${count})`,
    NO_RISKS: 'No risks linked yet',
    UNLINK: 'Unlink from this plan',
    ACTIONS: (done: number, total: number) => `Actions · ${done} of ${total} done`,
    NO_ACTIONS: 'No actions yet',
    ADD_ACTION: 'Add action',
    EDIT_ACTION: 'Edit action',
    ADD_OBJECTIVE: 'Add an objective',
  },
  FORM: {
    TITLE: 'New treatment plan',
    DESCRIPTION: 'One plan can treat several risks. Progress follows its actions.',
    NAME: 'Plan name',
    NAME_PLACEHOLDER: 'e.g. Backup and recovery hardening',
    OWNER: 'Plan owner',
    TARGET_DATE: 'Target date',
    OBJECTIVE: 'Objective',
    OBJECTIVE_PLACEHOLDER: 'What this plan achieves once complete',
    RISKS: 'Risks treated',
    SELECTED: (count: number) => `${count} selected`,
    ACTIONS: 'Actions',
    ACTION_PLACEHOLDER: 'Action',
    REMOVE_ACTION: 'Remove action',
    SELECT_DATE: 'Select a date',
    CANCEL: 'Cancel',
    CREATE: 'Create plan',
    ADD: 'Add',
    SAVE: 'Save',
  },
  PLAN: 'Treatment plan',
  PLAN_TITLE: (name: string, percent: number, status: string) => `${name} · ${percent}% · ${status}`,
  STATUS_PROGRESS: (status: string, percent: number) => `${status} · ${percent}%`,
  ASSIGN: {
    BUTTON: 'Assign',
    BULK: 'Add to treatment plan',
    TITLE: (count: number) => (count === 1 ? 'Add risk to a treatment plan' : `Add ${count} risks to a treatment plan`),
    PLAN_META: (owner: string | null, riskCount: number, percent: number) =>
      [owner, plural(riskCount, 'risk', 'risks'), `${percent}% done`].filter(Boolean).join(' · '),
    CREATE_NEW: 'Create a new plan with these risks',
    CONFIRM: 'Add to plan',
    ADDED: (code: string) => `Added to ${code}`,
  },
  DRAWER: {
    ADD: 'Add to plan',
    NONE: 'Not part of any treatment plan',
  },
  BULK_UPLOAD: {
    BUTTON: 'Import CSV',
    TITLE: 'Import treatment plans from CSV',
    DESCRIPTION:
      'Upload a CSV file with one row per action. Rows with the same plan name make one plan. Every row must be valid: if any row has errors, nothing is created and the errors are listed below.',
    UPLOAD_FILE: 'Upload CSV',
    UPLOADING: 'Uploading...',
    DOWNLOAD_SAMPLE: 'Download sample CSV',
    CANCEL: 'Cancel',
    SUCCESS: 'Treatment plans imported',
    VALIDATION_TABLE_ROW: 'Row',
    VALIDATION_TABLE_FIELD: 'Field',
    VALIDATION_TABLE_ERROR: 'Error',
    VALIDATION_TABLE_RECEIVED_VALUE: 'Received value',
    HEADER_ERROR_TITLE: 'Your CSV header has problems',
    HEADER_ERROR_DESCRIPTION:
      'Fix the header row in your file and upload it again. The accepted columns are listed below.',
    ACCEPTED_COLUMNS_REQUIRED: 'Required columns',
    ACCEPTED_COLUMNS_OPTIONAL: 'Optional columns',
    SUMMARY_LINE: '{totalRows} rows checked, {errorCount} errors found. Fix them in your CSV and upload it again.',
    ERROR_UNKNOWN_COLUMN: 'Unknown column: {field}',
    ERROR_MISSING_REQUIRED_COLUMN: 'Required column missing: {field}',
    ERROR_EMPTY_FILE: 'The file has a header but no treatment plan rows',
    ERROR_MALFORMED_ROW: 'The number of values in this row does not match the header',
    ERROR_REQUIRED: '{field} is required',
    ERROR_TOO_LONG: '{field} must be 255 characters or fewer',
    ERROR_INVALID_DATE: '{field} must be a date like 2027-03-31. Got "{receivedValue}"',
    ERROR_INVALID_ACTION_STATUS: 'Invalid action status "{receivedValue}". Allowed values: {allowedValues}',
    ERROR_OWNER_NOT_FOUND: 'User not found in this organization: "{receivedValue}"',
    ERROR_RISK_NOT_FOUND: 'Risk not found in this organization: "{receivedValue}"',
    ERROR_PLAN_MISMATCH: '{field} differs from the first row of this plan. Leave it empty or repeat the same value',
    ERROR_DUPLICATE_IN_DB: 'A treatment plan with this name already exists: "{receivedValue}"',
  },
  DELETE: {
    BUTTON: 'Delete plan',
    TITLE: 'Delete treatment plan',
    QUESTION: 'Are you sure you want to delete this treatment plan?',
    CONFIRMATION: 'This action cannot be undone.',
    CONFIRM: 'Delete',
  },
  LINK_RISKS_TITLE: (code: string) => `Link risks to ${code}`,
  CREATED: (code: string) => `${code} created`,
  DELETED: (code: string) => `${code} deleted`,
}
